import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Users, Settings, BookOpen, Star, BookMarked, Pencil, MoreVertical, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MessagePreviewCard } from '@/components/MessagePreviewCard';
import { ReportButton } from '@/components/ReportButton';
import { BookClubSuggestions } from '@/components/BookClubSuggestions';
import { useChats } from '@/hooks/useChats';
import { useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/hooks/useAuth';
import { useBookClubSuggestions } from '@/hooks/useBookClubSuggestions';
import { Message } from '@/types/chat';
import { formatDistanceToNow, format } from 'date-fns';
import { Link } from 'react-router-dom';

const ChatThread: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { chats, updateChatTitle, acceptMessageRequest, declineMessageRequest } = useChats();
  const { messages, loading, sendMessage, markAsRead } = useMessages(chatId || '');
  const { suggestions, loading: suggestionsLoading, updateSuggestionStatus, voteForSuggestion } = useBookClubSuggestions(chatId || '');
  
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const chat = chats.find(c => c.id === chatId);

  // Check if this is a pending message request for the current user
  const isMessageRequest = useMemo(() => {
    if (!chat || !user?.id) return false;
    const userMembership = chat.members?.find(m => m.user_id === user.id);
    return chat.is_message_request && userMembership && !userMembership.accepted_at;
  }, [chat, user?.id]);

  const handleAcceptRequest = async () => {
    if (!chatId) return;
    await acceptMessageRequest(chatId);
  };

  const handleDeclineRequest = async () => {
    if (!chatId) return;
    await declineMessageRequest(chatId);
    navigate('/inbox');
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (chatId && user) {
      markAsRead();
    }
  }, [chatId, user, markAsRead]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await sendMessage(newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleEditTitle = () => {
    setTitleInput(chat?.title || '');
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!chatId || !titleInput.trim()) return;
    
    try {
      await updateChatTitle(chatId, titleInput.trim());
      setEditingTitle(false);
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  };

  const getChatTitle = (title?: string, members?: any[]) => {
    if (title) return title;
    
    const otherMembers = members?.filter(m => m.user_id !== user?.id) || [];
    if (otherMembers.length === 1) {
      return otherMembers[0].user?.name || otherMembers[0].user?.username || 'Unknown';
    }
    return `Group (${otherMembers.length} members)`;
  };

  const getShareMessage = () => {
    return messages.find(m => m.type === 'share');
  };

  const renderShareCard = (sharePayload: any) => {
    return <MessagePreviewCard sharePayload={sharePayload} chatId={chatId} />;
  };

  const renderMessage = (message: Message) => {
    const isOwn = message.sender_id === user?.id;
    const sender = message.sender;

    return (
      <div
        key={message.id}
        className={`flex gap-3 mb-4 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={sender?.image || undefined} />
          <AvatarFallback>
            {sender?.name?.charAt(0) || sender?.username?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>

        <div className={`max-w-[70%] ${isOwn ? 'text-right' : 'text-left'}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium">
              {sender?.name || sender?.username}
            </span>
            <span className="text-xs text-muted-foreground">
              {format(new Date(message.created_at), 'HH:mm')}
            </span>
            {!isOwn && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <ReportButton
                      contentType="message"
                      contentId={message.id}
                      contentPreview={message.text || `Shared a ${message.share_payload?.kind}`}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start h-auto p-2 cursor-pointer"
                    />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div
            className={`p-3 rounded-lg group ${
              isOwn
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted'
            }`}
          >
            {message.type === 'share' ? (
              <div>
                <p className="text-sm mb-2">
                  Shared a {message.share_payload?.kind}
                </p>
                <MessagePreviewCard 
                  sharePayload={message.share_payload} 
                  chatId={chatId}
                />
              </div>
            ) : (
              <p className="text-sm">{message.text}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading chat...</div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Chat not found</h2>
            <p className="text-muted-foreground mb-4">The chat you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/inbox')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Inbox
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const shareMessage = getShareMessage();

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Sidebar for shared context (optional) */}
        {shareMessage && (
          <div className="w-80 border-r bg-muted/20 p-4 hidden lg:block">
            <div className="sticky top-4">
              <h3 className="font-semibold mb-4">Shared Books</h3>
              {renderShareCard(shareMessage.share_payload)}
            </div>
          </div>
        )}

        {/* Main chat area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/inbox')}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {chat.is_group ? <Users className="h-4 w-4" /> : null}
                  </AvatarFallback>
                </Avatar>
                
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-semibold flex items-center gap-2">
                      {getChatTitle(chat.title, chat.members)}
                      {chat.is_book_club && (
                        <Badge variant="secondary" className="gap-1">
                          <BookMarked className="w-3 h-3" />
                          Book Club
                        </Badge>
                      )}
                    </h1>
                    {chat.is_group && chat.created_by === user?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={handleEditTitle}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {chat.is_group && (
                    <p className="text-xs text-muted-foreground">
                      {chat.members?.length || 0} members
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {chat.is_book_club && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowSuggestions(!showSuggestions)}
                  >
                    <BookOpen className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Message Request Banner */}
          {isMessageRequest && (
            <div className="border-b p-4 bg-muted/50">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Message Request</AlertTitle>
                <AlertDescription>
                  This message is from someone you don't follow. Accept to move to your inbox.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleAcceptRequest}>Accept</Button>
                <Button size="sm" variant="outline" onClick={handleDeclineRequest}>Delete</Button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No messages yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Start the conversation!
                  </p>
                </div>
              ) : (
                messages.map(renderMessage)
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Message input */}
          <div className="border-t p-4">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                disabled={sending}
                className="flex-1"
              />
              <Button type="submit" disabled={!newMessage.trim() || sending}>
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Book Club Suggestions Sidebar */}
        {chat.is_book_club && showSuggestions && (
          <div className="w-96 border-l bg-muted/20">
            <BookClubSuggestions
              suggestions={suggestions}
              onUpdateStatus={updateSuggestionStatus}
              onVote={voteForSuggestion}
              loading={suggestionsLoading}
            />
          </div>
        )}
      </div>

      {/* Edit Title Dialog */}
      <Dialog open={editingTitle} onOpenChange={setEditingTitle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Group Name</DialogTitle>
          </DialogHeader>
          <Input
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            placeholder="Group name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveTitle();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTitle(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTitle} disabled={!titleInput.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatThread;